"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateOrUpdateBotFrameworkBotDriver = void 0;
const tslib_1 = require("tslib");
const typedi_1 = require("typedi");
const lib_1 = require("@feathersjs/hooks/lib");
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const isUUID_1 = tslib_1.__importDefault(require("validator/lib/isUUID"));
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../../error/common");
const IBotRegistration_1 = require("../../resource/botService/appStudio/interfaces/IBotRegistration");
const botFrameworkRegistration_1 = require("../../resource/botService/botRegistration/botFrameworkRegistration");
const common_2 = require("../../utils/common");
const constants_1 = require("../aad/utility/constants");
const addStartAndEndTelemetry_1 = require("../middleware/addStartAndEndTelemetry");
const invalidBotIdError_1 = require("./error/invalidBotIdError");
const actionName = "botFramework/create";
const helpLink = "https://aka.ms/teamsfx-actions/botFramework-create";
const botUrl = "https://dev.botframework.com/bots?id=";
let CreateOrUpdateBotFrameworkBotDriver = class CreateOrUpdateBotFrameworkBotDriver {
    description = (0, localizeUtils_1.getLocalizedString)("driver.botFramework.description");
    progressTitle = (0, localizeUtils_1.getLocalizedString)("driver.botFramework.progressBar.createOrUpdateBot");
    async run(args, context) {
        return (0, common_2.wrapRun)(async () => {
            const result = await this.handler(args, context);
            return result.output;
        }, actionName);
    }
    async execute(args, ctx) {
        let summaries = [];
        const outputResult = await (0, common_2.wrapRun)(async () => {
            const result = await this.handler(args, ctx);
            summaries = result.summaries;
            return result.output;
        }, actionName);
        return {
            result: outputResult,
            summaries,
        };
    }
    async handler(args, context) {
        try {
            this.validateArgs(args);
            if (!(0, isUUID_1.default)(args.botId)) {
                throw new invalidBotIdError_1.InvalidBotIdUserError(actionName, args.botId, helpLink);
            }
            let callingEndpoint = undefined;
            let configuredChannels = undefined;
            if (args.channels) {
                configuredChannels = [];
                for (const channel of args.channels) {
                    if (channel.name === IBotRegistration_1.BotChannelType.MicrosoftTeams) {
                        callingEndpoint = channel.callingWebhook;
                        configuredChannels.push(IBotRegistration_1.BotChannelType.MicrosoftTeams);
                    }
                    else if (channel.name === IBotRegistration_1.BotChannelType.M365Extensions) {
                        configuredChannels.push(IBotRegistration_1.BotChannelType.M365Extensions);
                    }
                }
            }
            const botRegistrationData = {
                botId: args.botId,
                name: args.name,
                description: args.description ?? "",
                iconUrl: args.iconUrl ?? "",
                messagingEndpoint: args.messagingEndpoint,
                callingEndpoint: callingEndpoint ?? "",
                configuredChannels,
                isSingleTenant: true,
            };
            const result = await (0, botFrameworkRegistration_1.createOrUpdateBotRegistration)(context.m365TokenProvider, botRegistrationData, context.logProvider);
            if (result.isErr()) {
                throw result.error;
            }
            return {
                output: new Map(),
                summaries: [
                    result.value
                        ? (0, localizeUtils_1.getLocalizedString)("driver.botFramework.summary.update", `${botUrl}${args.botId}`)
                        : (0, localizeUtils_1.getLocalizedString)("driver.botFramework.summary.create", `${botUrl}${args.botId}`),
                ],
            };
        }
        catch (error) {
            if (error instanceof teamsfx_api_1.UserError || error instanceof teamsfx_api_1.SystemError) {
                context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.failExecuteDriver, actionName, error.displayMessage));
                throw error;
            }
            const message = JSON.stringify(error);
            context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.failExecuteDriver, actionName, message));
            throw (0, common_1.assembleError)(error, actionName);
        }
    }
    validateArgs(args) {
        const invalidParameters = [];
        if (!args.botId || typeof args.botId !== "string") {
            invalidParameters.push("botId");
        }
        if (!args.name || typeof args.name !== "string") {
            invalidParameters.push("name");
        }
        if (!args.messagingEndpoint || typeof args.messagingEndpoint !== "string") {
            invalidParameters.push("messagingEndpoint");
        }
        if (args.description && typeof args.description !== "string") {
            invalidParameters.push("description");
        }
        if (args.iconUrl && typeof args.iconUrl !== "string") {
            invalidParameters.push("iconUrl");
        }
        if (args.channels) {
            if (!Array.isArray(args.channels)) {
                invalidParameters.push("channels");
            }
            else {
                for (const channel of args.channels) {
                    if (!channel.name ||
                        typeof channel.name !== "string" ||
                        (channel.name !== IBotRegistration_1.BotChannelType.MicrosoftTeams &&
                            channel.name !== IBotRegistration_1.BotChannelType.M365Extensions)) {
                        invalidParameters.push("channels");
                        break;
                    }
                    if (channel.name === IBotRegistration_1.BotChannelType.MicrosoftTeams) {
                        const callingWebhook = channel.callingWebhook;
                        if (callingWebhook && typeof callingWebhook !== "string") {
                            invalidParameters.push("channels");
                            break;
                        }
                    }
                }
            }
        }
        if (invalidParameters.length > 0) {
            throw new common_1.InvalidActionInputError(actionName, invalidParameters, helpLink);
        }
    }
};
exports.CreateOrUpdateBotFrameworkBotDriver = CreateOrUpdateBotFrameworkBotDriver;
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateOrUpdateBotFrameworkBotDriver.prototype, "run", null);
tslib_1.__decorate([
    (0, lib_1.hooks)([(0, addStartAndEndTelemetry_1.addStartAndEndTelemetry)(actionName, actionName)]),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], CreateOrUpdateBotFrameworkBotDriver.prototype, "execute", null);
exports.CreateOrUpdateBotFrameworkBotDriver = CreateOrUpdateBotFrameworkBotDriver = tslib_1.__decorate([
    (0, typedi_1.Service)(actionName) // DO NOT MODIFY the service name
], CreateOrUpdateBotFrameworkBotDriver);
//# sourceMappingURL=createOrUpdateBot.js.map